import shortid from 'shortid';
import { decodeHeader, CURRENT_SYNC } from 'shared/services/websocket/header';
import { CancelError } from 'shared/utils/cancelToken';
import { officeStore } from 'src/stores/index';

//TODO: support online/offline events

const options = { timeout: 4000, reconnectTimeout: 1000 * 60 * 60 }; // max reconnectTimeout is 120 mins due to AWS API Gateway socket implementions
class WSSimpleClient {
  #token = undefined;
  #ws = undefined;
  #connection = this.#createPromise();

  #attempts = 0;
  #connectedAt = null;
  #explicitlyClosed = false;

  #timeout = undefined;
  #reconnectTimer = undefined;
  #pendingReconnect = false;

  #tasks = new Map();
  _decompress = () => { throw new Error() } // Overrided in other class

  connect(token) {
    if (token !== this.#token) { this.disconnect() }

    this.#token = token;
    if (this.#ws) { return }

    this.#createSocket();
  }

  disconnect() {
    this.#cleanTasks();

    if (this.#ws) {
      this.#explicitlyClosed = true;
      this.#ws.close(1000);
      this.#ws = undefined;
    }
  }

  async action(action, payload, options = {}) {
    const key = shortid.generate();
    console.log('ws:action', key);

    return await this.#proceedTask(key, () => {
      console.log('ws:action:send', key, action);
      this.#ws.send(JSON.stringify({ action, key, payload }))
    }, options);
  }

  async waitFor(key, options = {}, callback) {
    console.debug('ws:waitFor', key);
    return await this.#proceedTask(key, callback, options);
  }

  get maximumPayloadSize() {
    const isIE = !window.document;
    return 1024 * (isIE ? 29 : 110); // API Geteway max size is 128Kb
  }

  async #proceedTask(key, callback, { cancelToken, ...options }) {
    const { promise, resolve, reject } = this.#createPromise();

    cancelToken?.once('abort', (reason) => {
      console.log('ws:proceedTask:abort', key);

      this.#tasks.delete(key);
      reject(new CancelError(reason));
    });

    if (this.#ws?.readyState !== 1) { await Promise.race([this.#connection.promise, promise]) }

    this.#tasks.set(key, { ...options, resolve, reject, payload: '' });
    callback && callback();

    return await promise;
  }

  #cleanTasks(error) {
    this.#tasks.forEach(({ reject }) => reject(error));
    this.#tasks.clear();
  }

  #onOpen = () => {
    console.debug('ws:onOpen');
    this.#connectedAt = new Date().getTime();

    this.#attempts = 0;
    this.#explicitlyClosed = false;
    this.#pendingReconnect = false;

    if (this.#reconnectTimer) { clearInterval(this.#reconnectTimer) }
    this.#reconnectTimer = setInterval(() => {
      const seconds = new Date().getTime() - this.#connectedAt;

      if (seconds >= options.reconnectTimeout) {
        this.#pendingReconnect = true;
        this.#reconnectIfNeed();
      }
    }, 10000);

    this.#connection.resolve();
    this.#connection = undefined;
  }

  #onMessage = ({ data: text }) => {
    const index = text.indexOf(':');
    const [, header = '', key] = text.substring(0, index).match(/^([^;]+);([^:]+)$/) ?? [];

    const { sync, fin, compressed, last, error } = decodeHeader(header);
    if (sync !== CURRENT_SYNC) { return console.error('ws:onMessage invalid version', sync) }

    console.debug(`ws:onMessage. Key: ${key}, compressed: ${compressed}, error: ${error}, last: ${last}, fin: ${fin}`);

    if (this.#tasks.has(key)) {
      const extra = this.#tasks.get(key);
      extra.payload = extra.payload.concat(text.substring(index + 1));

      if (fin) {
        const { payload, resolve, reject, callback } = extra;
        try {
          const data = JSON.parse(compressed ? this._decompress(payload) : payload);
          extra.payload = '';

          if (error) { throw data }
          callback && callback(data);

          if (last) {
            this.#tasks.delete(key);
            resolve(data);
          }
        }
        catch (error) {
          if (error.status === 503 && error.code === 'maintenance_mode') {
            officeStore.maintenanceMode = true;
          }
          console.error(`ws: ${key}`, error);
          this.#tasks.delete(key);
          reject(error);
        }
      }
    }

    this.#reconnectIfNeed();
  }

  #onClose = ({ code }) => {
    console.debug('ws:onClose:', code);

    this.#cleanTasks(new Error());
    if (this.#reconnectTimer) { clearInterval(this.#reconnectTimer) }

    if (this.#attempts === 0) { return this.#createSocket() }

    const delay = !this.#explicitlyClosed && Math.min(5000, Math.pow(1.5, this.#attempts) * 500);
    this.#triggerTimeout(this.#createSocket, delay);
  }

  #onError = (event) => {
    console.debug('ws:onError:', event);
  }

  #createSocket = () => {
    if (!this.#connection) {
      this.#connection = this.#createPromise();
    }

    const url = `${config.api.socketUrl}?t=${encodeURIComponent(this.#token ?? 'none')}`;
    this.#ws = new WebSocket(url);
    this.#attempts++;

    const events = { open: this.#onOpen, message: this.#onMessage, close: this.#onClose, error: this.#onError };
    Object.keys(events).forEach(key => this.#ws.addEventListener(key, events[key]));

    this.connectTimeout = setTimeout(() => { }, options.timeout);
  }

  #reconnectIfNeed() {
    if (this.#pendingReconnect && !this.#tasks.size) {
      console.debug('ws:reconnectIfNeed: reconnected', this.#ws.readyState);
      this.#pendingReconnect = false;
      this.#ws.close(4000);
    }
  }

  #triggerTimeout(callback, ms) {
    if (this.#timeout) { clearTimeout(this.#timeout) }
    this.#timeout = ms && setTimeout(callback, ms);
  }

  #createPromise() {
    const data = {};
    const promise = new Promise((resolve, reject) => Object.assign(data, { resolve, reject }));
    return { promise, ...data };
  }
}

const ws = new WSSimpleClient();
export { ws, WSSimpleClient }
